import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { Observable, combineLatest, map, take } from 'rxjs';
import { TooltipModule } from 'primeng/tooltip';
import { ChipModule } from 'primeng/chip';
import { GlobalFilterRepository } from '@store/global-filter.repository';
import { GlobalPropertyRepository } from '@store/global-properties.repository';
import { EntityRef } from '@core/models';
import { Owner, Region } from '../advanced-filter/advanced-filter.data';

interface FilterDisplayState {
  selectedEnterprises: EntityRef[];
  selectedOwners: Owner[];
  selectedRegions: Region[];
}

@Component({
  selector: 'cwa-applied-filter-bar',
  standalone: true,
  imports: [CommonModule, ButtonModule, ChipModule, TooltipModule],
  templateUrl: './applied-filter-bar.component.html',
})
export class AppliedFilterBarComponent implements OnInit {
  private globalFilterRepo = inject(GlobalFilterRepository);
  private globalPropRepo = inject(GlobalPropertyRepository);

  filterState$!: Observable<FilterDisplayState | null>;

  ngOnInit() {
    this.filterState$ = combineLatest([
      this.globalFilterRepo.filter$,
      this.globalPropRepo.enterprises$,
      this.globalPropRepo.owners$,
      this.globalPropRepo.regions$,
    ]).pipe(
      map(([filter, enterprises, owners, regions]) => {
        if (!filter) return null;

        // Match IDs from filter with full entity data
        const selectedEnterprises = enterprises.filter((e) => filter.enterprises.includes(e.id));
        const selectedOwners = owners.filter((o) => filter.ownershipGroups.includes(o.id));
        const selectedRegions = regions.filter((r) => filter.regions.includes(r.id));

        return {
          selectedEnterprises,
          selectedOwners,
          selectedRegions,
        };
      })
    );
  }

  clearAllFilters() {
    this.globalFilterRepo.setFilter({
      enterprises: [],
      ownershipGroups: [],
      brands: [],
      regions: [],
      hotels: [],
    });

    this.globalPropRepo.fetchEnterprises([], []).subscribe();

    this.globalPropRepo.fetchBrands([], [], []).subscribe();

    this.globalPropRepo.fetchHotels([], [], [], []).subscribe();
  }

  removeEnterprise(enterpriseId: string) {
    this.globalFilterRepo.filter$.pipe(take(1)).subscribe((filter) => {
      if (!filter) return;

      const updatedEnterprises = filter.enterprises.filter((id) => id !== enterpriseId);

      this.globalFilterRepo.setFilter({
        ...filter,
        enterprises: updatedEnterprises,
        brands: [],
        hotels: [],
      });

      this.globalPropRepo.fetchBrands(updatedEnterprises, []).subscribe();

      this.globalPropRepo.fetchHotels([], updatedEnterprises, [], []).subscribe();
    });
  }

  removeOwner(ownerId: string) {
    this.globalFilterRepo.filter$.pipe(take(1)).subscribe((filter) => {
      if (!filter) return;

      const updatedOwners = filter.ownershipGroups.filter((id) => id !== ownerId);

      // When owner is removed, reset children (not enterprise parent)
      this.globalFilterRepo.setFilter({
        ...filter,
        ownershipGroups: updatedOwners,
        enterprises: [],
        brands: [],

        hotels: [],
      });

      // Re-fetch children filtered by remaining owners + regions (enterprise was reset above)
      this.globalPropRepo.fetchEnterprises(updatedOwners, filter.regions).subscribe();
      this.globalPropRepo.fetchBrands([], updatedOwners, filter.regions).subscribe();
      this.globalPropRepo.fetchHotels([], [], updatedOwners, filter.regions).subscribe();
    });
  }

  removeRegion(regionId: string) {
    this.globalFilterRepo.filter$.pipe(take(1)).subscribe((filter) => {
      if (!filter) return;

      const updatedRegions = filter.regions.filter((id) => id !== regionId);

      this.globalFilterRepo.setFilter({
        ...filter,
        regions: updatedRegions,
        enterprises: [],
        brands: [],
        hotels: [],
      });

      // Re-fetch hotels filtered by all parents + remaining regions
      this.globalPropRepo.fetchEnterprises(filter.ownershipGroups, updatedRegions).subscribe();
      this.globalPropRepo.fetchBrands([], filter.ownershipGroups, updatedRegions).subscribe();
      this.globalPropRepo.fetchHotels([], [], filter.ownershipGroups, updatedRegions).subscribe();
    });
  }

  hasActiveFilters(state: FilterDisplayState | null): boolean {
    return !!(
      state?.selectedEnterprises?.length ||
      state?.selectedOwners?.length ||
      state?.selectedRegions?.length
    );
  }

  // Helper functions for chip count display
  hasRemainingItems(
    state: FilterDisplayState | null,
    type: 'enterprises' | 'owners' | 'regions'
  ): boolean {
    if (!state) return false;
    const items = this.getItemsArray(state, type);
    return items ? items.length > 2 : false;
  }

  getRemainingCount(
    state: FilterDisplayState | null,
    type: 'enterprises' | 'owners' | 'regions'
  ): string {
    if (!state) return '+0';
    const items = this.getItemsArray(state, type);
    const count = items ? items.length - 2 : 0;
    return `+${count}`;
  }

  private getItemsArray(
    state: FilterDisplayState,
    type: 'enterprises' | 'owners' | 'regions'
  ): any[] | null {
    switch (type) {
      case 'enterprises':
        return state.selectedEnterprises;
      case 'owners':
        return state.selectedOwners;
      case 'regions':
        return state.selectedRegions;
      default:
        return null;
    }
  }
}
